import _ from 'lodash'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { InputField } from 'Components/reusable/InputFields/InputField'
import { Id } from 'types/ids'
import { Context } from '../context'
import { AnyObject, ContextValue, EditableFieldsOfType } from '../types'

/**
 * Password field.
 */
type NullableNumber = number | null

export const DataEditContextNullableNumberField = <T extends AnyObject>({
  field,
  ...rest
}: { field: EditableFieldsOfType<T, NullableNumber, Id> } & Omit<
  Record<string, unknown>,
  'onChange' | 'value' | 'error' | 'name'
>) => {
  const { flatData, errors, disabled, setFields, handleSubmit, localeBase, t } = useContext<ContextValue<T>>(Context)

  const [value, setValue] = useState<string>((flatData[field] as unknown as NullableNumber)?.toString() ?? '')
  const [focused, setFocused] = useState<boolean>(false)
  const error = errors[field]

  const setField = useMemo(
    () =>
      _.debounce((newValue: string) => {
        const numberValue = newValue === '' ? null : parseFloat(newValue)
        setFields({ [field]: numberValue })
      }, 300),
    [setFields, field]
  )

  useEffect(() => {
    if (!focused) {
      setValue((flatData[field] as unknown as NullableNumber)?.toString() ?? '')
    }
  }, [field, flatData, focused])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setValue(newValue)
    setField(newValue)
  }

  return (
    <InputField
      onKeyDown={e => handleSubmit(e)}
      onChange={handleChange}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      type="number"
      value={value}
      error={error}
      disabled={disabled[field]}
      name={field}
      label={t(`${localeBase}.${field}`)}
      {...rest}
    />
  )
}
